import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ModernADUDesignPacifica = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Modern ADU Design & Construction in Pacifica",
    "description": "Create stunning Modern ADUs in Pacifica with ocean-inspired design, sustainable materials, and coastal-resistant construction.",
    "url": "https://www.udaadu.com/modern-adu-design-pacifica",
    "mainEntity": {
      "@type": "Service",
      "name": "Modern ADU Design & Construction",
      "provider": {
        "@type": "Organization",
        "name": "UDA ADU"
      },
      "areaServed": "Pacifica, CA"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/modern-adu-design-pacifica" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Modern ADU Design & Construction in Pacifica
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Create stunning Modern ADUs in Pacifica with ocean-inspired design, sustainable materials, and coastal-resistant construction.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center mb-16">
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Get Free Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Calculate Costs</Link>
          </Button>
        </div>

        {/* Why Choose Modern ADUs */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Why Choose Modern ADUs in Pacifica?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Coastal Integration</h3>
              <p className="text-muted-foreground">
                Modern designs seamlessly integrate with Pacifica's stunning coastal environment, maximizing ocean views and natural beauty.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Weather Resilience</h3>
              <p className="text-muted-foreground">
                Advanced materials and construction techniques protect against salt air, moisture, and coastal weather conditions.
              </p>
            </div>
            <div className="text-center p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Sustainable Living</h3>
              <p className="text-muted-foreground">
                Eco-friendly materials and energy-efficient systems align with Pacifica's environmental consciousness and sustainability goals.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Modern ADU Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Exterior Elements</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Flat roofs with rooftop deck potential</li>
                <li>• Floor-to-ceiling windows and sliding doors</li>
                <li>• Weather-resistant siding and finishes</li>
                <li>• Geometric shapes and angular designs</li>
                <li>• Outdoor terraces and coastal view optimization</li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-foreground">Interior Design</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>• Open layouts with flexible living spaces</li>
                <li>• High ceilings and natural light emphasis</li>
                <li>• Modern kitchens with sleek appliances</li>
                <li>• Smart home automation systems</li>
                <li>• Sustainable materials and finishes</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Pacifica Building Considerations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Coastal Zone Regulations</h3>
              <p className="text-muted-foreground">
                Pacifica's coastal location requires compliance with California Coastal Commission guidelines and special environmental protections for new construction.
              </p>
            </div>
            <div className="p-6 border border-border rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Salt Air Protection</h3>
              <p className="text-muted-foreground">
                Modern materials specifically selected for coastal environments ensure longevity and minimal maintenance in Pacifica's salt air conditions.
              </p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-foreground">Investment Range</h2>
          <div className="text-center p-8 border border-border rounded-lg">
            <p className="text-2xl font-bold mb-4 text-foreground">$240,000 - $420,000</p>
            <p className="text-muted-foreground">
              Modern ADUs in coastal areas require specialized materials and construction methods. Premium finishes and sustainable features add value.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-8 text-foreground">Start Your Modern ADU Project</h2>
          <p className="text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
            Ready to create a modern coastal retreat on your Pacifica property? Let's design your perfect ADU.
          </p>
          <Button onClick={openCalendlyModal} size="lg" className="mr-4">
            Schedule Consultation
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/adu-calculator">Get Cost Estimate</Link>
          </Button>
        </section>

        {/* Internal Links */}
        <section className="text-center">
          <h3 className="text-2xl font-semibold mb-6 text-foreground">Explore Other ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild variant="outline">
              <Link to="/contemporary-adu-design-pacifica">Contemporary ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/coastal-adu-design-pacifica">Coastal ADUs</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/sustainable-adu-design-pacifica">Sustainable ADUs</Link>
            </Button>
          </div>
        </section>
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default ModernADUDesignPacifica;